import { useState } from 'react'
import styled from '@emotion/styled'
import {
  closeModal,
  openModal,
  selectCustomer,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import {
  ButtonLink,
  Button,
  Heading,
  Text,
  ModalContent,
  ModalView,
  QRCode,
} from 'components'
import RewardImage from './RewardImage'
import { RewardEnhanced, makeLimitations } from '../../Reward/Reward'

const RewardView = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0;
  text-align: center;
`

const RewardHeader = styled.div`
  margin: 0 0 1rem;

  & > p {
    margin: 0;
  }

  p:first-of-type {
    font-size: ${(props) => props.theme.fonts.sizes.h3};
    line-height: 1.1;
    margin: 0;
  }

  p + p {
    font-size: ${(props) => props.theme.fonts.sizes.small};
    line-height: ${(props) => props.theme.fonts.body.lineHeight};
    margin: 1rem 0 0;
  }
`

const RewardFinePrint = styled.div`
  & > p {
    font-size: ${(props) => props.theme.fonts.sizes.xSmall};
    line-height: ${(props) => props.theme.fonts.body.lineHeight};
    // margin: 0 !important;
  }
`

const RewardContent = styled.div`
  label: RewardContent;
  width: 100%;
  margin: 1.5rem 0 1rem;

  p {
    font-size: ${(props) => props.theme.fonts.sizes.xSmall};
    line-height: ${(props) => props.theme.fonts.body.lineHeight};

    button {
      margin: 1rem 0 0;
    }
  }
`

const RewardQRCodeView = styled.div`
  width: 16rem;
  margin: 1rem auto;
`

const RewardNote = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 0 0.5rem;
  font-size: ${(props) => props.theme.fonts.sizes.xSmall};

  span {
    display: block;
    line-height: 1.4;
  }
`

export interface RewardModalProps extends Record<string, unknown> {
  reward: RewardEnhanced
}

const Reward = ({ reward }: RewardModalProps) => {
  const dispatch = useAppDispatch()
  const [qrCodeUrl] = useState(null)
  const { title, description, imageUrl, expiration, service_type } = reward
  const limitations = makeLimitations(reward)
  const { profile } = useAppSelector(selectCustomer)
  const { customer_id, is_verified } = profile || {}

  const signUp = () => {
    dispatch(closeModal())
    setTimeout(() => {
      dispatch(openModal({ type: 'signUp' }))
    }, 250)
  }

  return (
    <ModalView style={{ maxWidth: '36rem' }}>
      <ModalContent>
        <RewardView>
          <RewardHeader>
            <Heading as="p">{title}</Heading>
            {description && <p>{description}</p>}
          </RewardHeader>
          <RewardFinePrint>
            {expiration ? (
              <Text color="alert" size="small" as="p">
                Use by {expiration}
              </Text>
            ) : null}
            {reward.per_order === 1 && (
              <Text color="alert" size="small" as="p">
                Cannot be used with any other discounts
              </Text>
            )}
            {reward.auth_type === 'ACCOUNT' && !customer_id && (
              <>
                <Text color="alert" size="small" as="p">
                  Account required to redeem this discount.
                </Text>
                <p>
                  <ButtonLink onClick={signUp}>
                    Click here to sign up.
                  </ButtonLink>
                </p>
              </>
            )}
            {reward.auth_type === 'VERIFIED' && !is_verified && (
              <Text color="alert" size="small" as="p">
                Verified account required to redeem this discount.
              </Text>
            )}
          </RewardFinePrint>
          <RewardContent>
            {qrCodeUrl ? (
              <>
                <p>Scan to redeem in-store</p>
                <RewardQRCodeView>
                  <QRCode src={qrCodeUrl} alt={title} />
                </RewardQRCodeView>
              </>
            ) : imageUrl ? (
              <RewardImage src={imageUrl} alt={title} />
            ) : null}
            <RewardNote>{limitations}</RewardNote>
            {service_type !== 'WALKIN' && (
              <p>
                To redeem online, add the relevant items to your cart and apply
                this reward on the Checkout page
              </p>
            )}
          </RewardContent>
          <div>
            <Button color="secondary" onClick={() => dispatch(closeModal())}>
              Close
            </Button>
          </div>
        </RewardView>
      </ModalContent>
    </ModalView>
  )
}

export default Reward
